import { Container, Graphics } from 'pixi.js';
import { COLORS, DESIGN_W, BORDER } from '../app/theme';
import { viewport } from '../app/viewport';
import { makeText, Button } from './widgets';
import { GameTray, TRAY_TITLE } from './gameTray';
import { handRow } from './cardSprite';
import { scaleIn } from '../app/anim';
import { toPersianDigits } from '../util/persian';
import type { Atlas } from '../assets/AtlasLoader';

export interface RoundResultView {
  atlas: Atlas | null;
  myWon: boolean;
  myHand: string[];
  myValue: number;
  oppHand: string[];
  oppValue: number;
  /** Persian reason (already localized by the server). */
  reason: string;
  /** Optional neutral spectator copy; player views keep the default win/lose wording. */
  title?: string;
  myLabel?: string;
  oppLabel?: string;
  /** Called when the user taps بستن (close). */
  onClose?: () => void;
}

const WIN_GREEN = 0x4ade80;
const RESULT_CARD_W = 72;
const RESULT_HAND_MAX_W = 292;

/** A rounded value pill (green outline = winner, red = loser), mirroring the Unity result panel. */
function valuePill(value: number, won: boolean, cx: number, y: number): Container {
  const c = new Container();
  const w = 150;
  const h = 46;
  const color = won ? WIN_GREEN : COLORS.danger;
  const g = new Graphics().roundRect(-w / 2, 0, w, h, 14).fill({ color: COLORS.darkPill, alpha: 0.95 }).stroke({ width: BORDER.thick, color });
  c.addChild(g);
  const t = makeText(toPersianDigits(value), { size: 26, weight: '700', align: 'center', fill: COLORS.white });
  t.anchor.set(0.5);
  t.position.set(0, h / 2);
  c.addChild(t);
  c.position.set(cx, y);
  return c;
}

/** Adds a centered overlapped hand row to `parent`, centered on `cx` at `y`. */
function addHand(parent: Container, atlas: Atlas | null, codes: string[], cx: number, y: number): void {
  const naturalStep = RESULT_CARD_W * 0.6;
  const step = codes.length > 1
    ? Math.max(Math.min(naturalStep, (RESULT_HAND_MAX_W - RESULT_CARD_W) / (codes.length - 1)), RESULT_CARD_W * 0.18)
    : 0;
  const overlap = 1 - step / RESULT_CARD_W;
  const width = codes.length ? RESULT_CARD_W + (codes.length - 1) * step : 0;
  const row = handRow(atlas, codes, RESULT_CARD_W, overlap);
  row.position.set(cx - width / 2, y);
  parent.addChild(row);
}

/**
 * The round-result panel — Unity's RoundResultPanel that slides down from the top: a win/lose
 * headline, both revealed hands with colored value pills (my hand left, opponent right), the Persian
 * reason, and a بستن close. Returns a Container; the scene adds it to its overlay and removes it on
 * the next GetGame (or the fallback timeout). The win/lose VFX is fired by the scene, not here.
 */
export function roundResultOverlay(v: RoundResultView): Container {
  const c = new Container();
  const dim = new Graphics().rect(0, 0, DESIGN_W, viewport.h).fill({ color: COLORS.scrim, alpha: 0.35 });
  c.addChild(dim);

  const trayW = DESIGN_W;
  const trayH = 392;
  const tray = new GameTray(trayW, trayH, 'top');
  c.addChild(tray);
  const panel = tray.content;

  const title = makeText(v.title ?? (v.myWon ? 'شما بردید 🎉' : 'شما باختید'), {
    size: 34,
    weight: '700',
    align: 'center',
    fill: v.title ? COLORS.gold : v.myWon ? WIN_GREEN : COLORS.danger,
  });
  title.anchor.set(0.5, 0);
  title.position.set(trayW / 2, 18);
  panel.addChild(title);

  const close = new Button('بستن', () => v.onClose?.(), { width: 120, height: 46, fontSize: 20 });
  close.position.set(trayW - 120 - 16, 16);
  panel.addChild(close);

  const myCx = trayW * 0.28;
  const oppCx = trayW * 0.72;

  const myLabel = makeText(v.myLabel ?? 'شما', { size: 20, fill: TRAY_TITLE, align: 'center' });
  myLabel.anchor.set(0.5, 0);
  myLabel.position.set(myCx, 78);
  panel.addChild(myLabel);
  const oppLabel = makeText(v.oppLabel ?? 'حریف', { size: 20, fill: TRAY_TITLE, align: 'center' });
  oppLabel.anchor.set(0.5, 0);
  oppLabel.position.set(oppCx, 78);
  panel.addChild(oppLabel);

  addHand(panel, v.atlas, v.myHand, myCx, 108);
  addHand(panel, v.atlas, v.oppHand, oppCx, 108);

  panel.addChild(valuePill(v.myValue, v.myWon, myCx, 230));
  panel.addChild(valuePill(v.oppValue, !v.myWon, oppCx, 230));

  const reasonBg = new Graphics()
    .roundRect(40, 300, trayW - 80, 64, 14)
    .fill({ color: COLORS.darkPill, alpha: 0.92 })
    .stroke({ width: BORDER.thin, color: COLORS.panelBorder, alpha: 0.5 });
  panel.addChild(reasonBg);
  const reason = makeText(v.reason, { size: 22, align: 'center', fill: COLORS.gold });
  reason.style.wordWrap = true;
  reason.style.wordWrapWidth = trayW - 120;
  reason.anchor.set(0.5);
  reason.position.set(trayW / 2, 332);
  panel.addChild(reason);

  tray.show();
  scaleIn(title, 300, 0.7);
  return c;
}
